import { prefersReducedMotion } from './prefersReducedMotion.js';

/**
 * Snap-stepped horizontal timeline. Pins the section's viewport at the top of
 * the window for `N × 100vh` of vertical scroll, but — crucially — never snaps
 * the *scroll position*. Lenis owns scroll on this theme (smoothWheel, ~1.15s
 * duration); animating scrollTop to a snap point fought Lenis and smeared the
 * settle into a long laggy glide. So instead scroll runs free and we snap the
 * *visual* discretely: ScrollTrigger.onUpdate rounds progress to a card index,
 * and when that index changes we tween a single `focus` proxy to it. Lenis
 * never sees a programmatic scroll, so there's nothing for it to smear.
 *
 * `applyFocus` is the one writer: from `focus` it derives the track/rail x, the
 * rail-fill scaleX, and per-card opacity / per-dot scale / per-pill width. One
 * tween drives `focus`, so the slide, fill, dots, and pills all move together
 * during a jump and sit perfectly still between jumps.
 *
 * Ported from the React `AboutTimelineSection`. Layout constants live in
 * `_timeline.scss`; this file only owns motion.
 */
export default () => ({
  cleanup: null,

  init() {
    const section = this.$el;
    const viewport = section.querySelector('.timeline__viewport');
    const track = section.querySelector('.timeline__track');
    const rail = section.querySelector('.timeline__rail');
    const railFill = section.querySelector('.timeline__rail-fill');
    const cards = section.querySelectorAll('.timeline-item__inner');
    const railDots = section.querySelectorAll('.timeline__rail-dot');
    const pills = section.querySelectorAll('.timeline__pill');

    const N = Number(section.dataset.itemCount) || cards.length || 1;

    // Writes derived per-frame state (cards / dots / pills) for a given
    // normalized scroll progress. Pulled out so the reduced-motion + N<=1
    // branches can run it once to set the resting state.
    const writeScrubState = (progress) => {
      const focus = progress * (N - 1);
      cards.forEach((card, i) => {
        const dist = Math.abs(focus - i);
        const opacity = Math.max(0.28, 1 - Math.max(0, dist - 0.18));
        window.gsap.set(card, { opacity });
      });
      railDots.forEach((dot, i) => {
        const dist = Math.abs(focus - i);
        window.gsap.set(dot, { scale: dist < 0.35 ? 1.4 : 1 });
      });
      pills.forEach((pill, i) => {
        const dist = Math.abs(focus - i);
        const active = dist < 0.5;
        window.gsap.set(pill, {
          width: active ? 20 : 6,
          opacity: active ? 1 : 0.28,
        });
      });
    };

    // Static fallback paths:
    //  - N=1: nothing to scrub; just show the one card focused.
    //  - prefers-reduced-motion: no pin, no scrub; pin would feel awful with
    //    no perceived motion to justify the scroll capture. First card stays
    //    focused; the section just takes 100vh of vertical space.
    if (N <= 1 || prefersReducedMotion()) {
      window.gsap.set(track, { x: 0 });
      window.gsap.set(rail, { x: 0 });
      window.gsap.set(railFill, { scaleX: 0 });
      if (prefersReducedMotion() && N > 1) {
        // No scrub to bring later cards into focus, so the writeScrubState(0)
        // dimming would strand every non-first card's text at opacity 0.28 —
        // an AA contrast failure with no way to recover. Present all cards
        // (and their dots/pills) at full opacity instead.
        cards.forEach((card) => window.gsap.set(card, { opacity: 1 }));
        railDots.forEach((dot) => window.gsap.set(dot, { scale: 1 }));
        pills.forEach((pill) => window.gsap.set(pill, { width: 6, opacity: 1 }));
      } else {
        writeScrubState(0);
      }
      // Collapse the runway since we're not pinning — no need for N×100vh.
      section.style.height = '100vh';
      return;
    }

    // Responsive: separate matchMedia branches so the desktop / mobile
    // `--step-vw` value lands on the right `x` end-state. matchMedia handles
    // cleanup automatically when the breakpoint changes, plus its master
    // `revert()` is what we call on destroy.
    const mm = window.gsap.matchMedia();
    this.mm = mm;

    const buildSnap = (stepVw) => {
      // The single source of truth for "where are we." Integer = a card is
      // centered; fractional only while a jump tween is in flight.
      const state = { focus: 0 };

      // The one writer. Derives every animated property from `state.focus` so a
      // single tween keeps the slide, rail fill, opacity, dots, and pills in
      // lockstep. progress = focus / (N-1) feeds writeScrubState's own focus
      // math unchanged.
      const applyFocus = () => {
        const f = state.focus;
        const progress = f / (N - 1);
        window.gsap.set([track, rail], { x: `${-f * stepVw}vw` });
        window.gsap.set(railFill, { scaleX: progress });
        writeScrubState(progress);
      };

      let currentIndex = 0;

      const st = window.ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: () => `+=${(N - 1) * window.innerHeight}`,
        pin: viewport,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Advance the cards over the middle 5%–95% of the pin, clamped at both
          // ends. This gives a brief hold on the first card before it starts
          // stepping and on the last card before the pin releases — without
          // changing the total pin length. Round, not floor: the card flips at
          // the midpoint between two cards, so each stays focused across its
          // share of scroll. No scroll-position snap — Lenis is untouched.
          const cardProgress = Math.min(1, Math.max(0, (self.progress - 0.05) / 0.9));
          const targetIndex = Math.round(cardProgress * (N - 1));
          if (targetIndex === currentIndex) return;
          currentIndex = targetIndex;
          // overwrite so a fast scroll past several cards retargets the live
          // tween instead of stacking — it slides straight to the new card.
          window.gsap.to(state, {
            focus: targetIndex,
            duration: 0.75,
            ease: 'power3.out',
            overwrite: true,
            onUpdate: applyFocus,
          });
        },
      });

      // Seed the resting state so the first card renders correctly pre-scroll.
      applyFocus();

      return () => {
        window.gsap.killTweensOf(state);
        st.kill();
      };
    };

    // Boundaries match the CSS `lg` breakpoint (--breakpoint-lg: 960px). The
    // step values mirror `--step-vw` per breakpoint; both must switch together
    // or the JS track translate (vw) desyncs from the CSS card positions.
    mm.add('(min-width: 960px)', () => buildSnap(47));
    mm.add('(max-width: 959px)', () => buildSnap(100));
  },

  destroy() {
    // Theme-editor re-renders can re-mount this element; revert all matchMedia
    // contexts so we don't leak ScrollTriggers across edits.
    this.mm?.revert();
    this.mm = null;
  },
});
